import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageMinus, Plus, Trash2, XCircle } from "lucide-react";
import { api, apiError, num, dateTime } from "@/lib/api";
import { AppShell, PageHeader, Panel, EmptyState, SkeletonRows, Btn, Tag } from "@/components/Shell";

const STATUS_TONE = { open: "amber", part_issued: "blue", issued: "green", cancelled: "slate" };
const STATUS_LABEL = { open: "committed", part_issued: "part issued", issued: "issued", cancelled: "cancelled" };

export default function Sales() {
  const [sales, setSales] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ reference: "", customer_name: "", note: "" });
  const [lines, setLines] = useState([{ product_id: "", quantity: 10 }]);
  const [issue, setIssue] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [s, p] = await Promise.all([
      api.get("/sales"),
      api.get("/products", { params: { page: 1, page_size: 100 } }),
    ]);
    setSales(s.data);
    setProducts(p.data.items);
    setLines((ls) => ls.map((l) => ({ ...l, product_id: l.product_id || p.data.items[0]?.id || "" })));
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/sales", {
        reference: form.reference,
        customer_name: form.customer_name,
        note: form.note,
        lines: lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) })),
      });
      toast.success(`${data.reference} committed ${num(data.ordered_units)} unit(s)`);
      setForm({ reference: "", customer_name: "", note: "" });
      setLines([{ product_id: products[0]?.id || "", quantity: 10 }]);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const issueLine = async (sale, line, qty) => {
    if (!qty || Number(qty) <= 0) return;
    setBusy(`issue-${line.product_id}`);
    try {
      const { data } = await api.post(`/sales/${sale.id}/issue`, {
        lines: [{ product_id: line.product_id, quantity: Number(qty) }],
      });
      toast.success(`${qty} × ${line.sku} taken out of stock against ${data.reference}`);
      setIssue((s) => ({ ...s, [`${sale.id}-${line.product_id}`]: "" }));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const issueAll = async (sale) => {
    setBusy(`all-${sale.id}`);
    try {
      await api.post(`/sales/${sale.id}/issue`, {
        lines: sale.lines.filter((l) => l.outstanding > 0)
          .map((l) => ({ product_id: l.product_id, quantity: l.outstanding })),
      });
      toast.success(`${sale.reference} fully issued`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const cancel = async (sale) => {
    try {
      const { data } = await api.post(`/sales/${sale.id}/cancel`);
      toast.success(`${sale.reference} cancelled — ${num(data.released)} unit(s) released`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Sales & issues"
        subtitle="Type any sale reference to commit stock, then take stock out against it — part-issues keep a running balance per line."
        testId="sales-header"
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <Panel title="Commit stock to a sale" testId="sale-form-panel" className="xl:col-span-4">
          <div className="space-y-3 p-4">
            <label className="block text-sm">
              Sale reference
              <input
                data-testid="sale-reference-input"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="SO-1042 / Ted's order / eBay-8891"
                list="open-sale-refs"
                className="mono mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              />
              <datalist id="open-sale-refs">
                {(sales || []).filter((s) => s.status === "open" || s.status === "part_issued")
                  .map((s) => <option key={s.id} value={s.reference} />)}
              </datalist>
              <span className="mt-1 block text-xs text-slate-500">
                Reusing an open reference tops up the same sale.
              </span>
            </label>
            <label className="block text-sm">
              Customer <span className="text-slate-400">(optional)</span>
              <input
                data-testid="sale-customer-input"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              />
            </label>

            {lines.map((line, i) => (
              <div key={i} className="flex items-end gap-2">
                <label className="flex-1 text-sm">
                  {i === 0 && "Lines"}
                  <select
                    data-testid={`sale-line-product-${i}`}
                    value={line.product_id}
                    onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, product_id: e.target.value } : l)))}
                    className="mono mt-1 w-full border border-slate-300 bg-white px-2 py-2 text-xs focus:border-slate-900 focus:outline-none"
                  >
                    {products.map((p) => (<option key={p.id} value={p.id}>{p.sku} — {p.name}</option>))}
                  </select>
                </label>
                <input
                  data-testid={`sale-line-qty-${i}`}
                  type="number"
                  min="1"
                  value={line.quantity}
                  onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, quantity: e.target.value } : l)))}
                  className="mono w-20 border border-slate-300 px-2 py-2 text-xs focus:border-slate-900 focus:outline-none"
                />
                {lines.length > 1 && (
                  <button data-testid={`sale-remove-line-${i}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <Btn variant="ghost" data-testid="sale-add-line" onClick={() => setLines((ls) => [...ls, { product_id: products[0]?.id || "", quantity: 5 }])}>
              <Plus className="h-4 w-4" /> Add line
            </Btn>
            <Btn className="w-full justify-center" data-testid="sale-create-button" disabled={busy === "create" || !form.reference.trim()} onClick={create}>
              {busy === "create" ? "Committing…" : "Commit to sale"}
            </Btn>
            <p className="text-xs text-slate-500">
              Committing moves stock into Committed and drops Available — exactly as a Xero
              invoice does. On Hand only falls when you issue.
            </p>
          </div>
        </Panel>

        <Panel title="Sales" testId="sales-list-panel" className="xl:col-span-8">
          {!sales ? (
            <SkeletonRows rows={5} />
          ) : sales.length === 0 ? (
            <EmptyState icon={PackageMinus} title="No sales yet" body="Commit stock to a sale reference, then issue against it as goods leave." testId="sales-empty" />
          ) : (
            <div className="divide-y divide-slate-100">
              {sales.map((sale) => (
                <div key={sale.id} className="p-4" data-testid={`sale-row-${sale.reference}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="mono text-sm">{sale.reference}</span>
                        <Tag tone={STATUS_TONE[sale.status]} testId={`sale-status-${sale.reference}`}>
                          {STATUS_LABEL[sale.status]}
                        </Tag>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {sale.customer_name || "No customer"} · {dateTime(sale.created_at)} · {sale.user_email}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-right">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-slate-500">Ordered</p>
                        <p className="num text-sm">{num(sale.ordered_units)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-slate-500">Issued</p>
                        <p className="num text-sm text-blue-600">{num(sale.issued_units)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-slate-500">Outstanding</p>
                        <p className="num text-sm text-amber-600" data-testid={`sale-outstanding-${sale.reference}`}>{num(sale.outstanding_units)}</p>
                      </div>
                      {sale.outstanding_units > 0 && sale.status !== "cancelled" && (
                        <>
                          <Btn data-testid={`sale-issue-all-${sale.reference}`} disabled={busy === `all-${sale.id}`} onClick={() => issueAll(sale)}>
                            Issue all
                          </Btn>
                          <Btn variant="danger" data-testid={`sale-cancel-${sale.reference}`} onClick={() => cancel(sale)}>
                            <XCircle className="h-4 w-4" /> Cancel
                          </Btn>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">SKU</th>
                          <th className="px-3 py-1.5 text-right">Ordered</th>
                          <th className="px-3 py-1.5 text-right">Issued</th>
                          <th className="px-3 py-1.5 text-right">Outstanding</th>
                          <th className="px-3 py-1.5 text-right">Issue now</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sale.lines.map((line) => (
                          <tr key={line.product_id} data-testid={`sale-line-${sale.reference}-${line.sku}`}>
                            <td className="mono px-3 py-1.5">
                              {line.sku}
                              {line.via_bundle && <span className="ml-2 text-[10px] text-violet-600">via {line.via_bundle}</span>}
                            </td>
                            <td className="num px-3 py-1.5 text-right">{num(line.quantity)}</td>
                            <td className="num px-3 py-1.5 text-right text-blue-600">{num(line.issued)}</td>
                            <td className="num px-3 py-1.5 text-right text-amber-600">{num(line.outstanding)}</td>
                            <td className="px-3 py-1.5 text-right">
                              {line.outstanding > 0 && sale.status !== "cancelled" ? (
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    data-testid={`sale-issue-qty-${sale.reference}-${line.sku}`}
                                    type="number"
                                    min="1"
                                    max={line.outstanding}
                                    value={issue[`${sale.id}-${line.product_id}`] ?? ""}
                                    onChange={(e) => setIssue((s) => ({ ...s, [`${sale.id}-${line.product_id}`]: e.target.value }))}
                                    placeholder={String(line.outstanding)}
                                    className="mono w-20 border border-slate-300 px-2 py-1 text-right text-xs focus:border-slate-900 focus:outline-none"
                                  />
                                  <Btn
                                    variant="outline"
                                    className="px-2 py-1 text-xs"
                                    data-testid={`sale-issue-button-${sale.reference}-${line.sku}`}
                                    disabled={busy === `issue-${line.product_id}`}
                                    onClick={() => issueLine(sale, line, issue[`${sale.id}-${line.product_id}`] || line.outstanding)}
                                  >
                                    Issue
                                  </Btn>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
